import { useCallback, useEffect, useState } from 'react';

import { PaginationState } from '@tanstack/react-table';
import { Link, useParams } from 'react-router-dom';

import { DocChronRow } from './components/doc-chron-row';
import { DocChronRowSkeleton } from './components/doc-chron-row-skeleton';
import {
  DocChronologyDownloadColumn,
  DocChronologyDownloadColumnOptions,
} from '../../../../../../../../../../backend/src/matter/subset/chronology/doc-chronology/doc-chronology-download';
import {
  DownloadFormat,
  StyleFormat,
  DocumentDownloadOptions,
} from '../../../../../../../../../../backend/src/types/types';
import useDocChronologyDownload from '@/api/mutations/useDownloadDocChronology';
import { useUpdateDocChronology } from '@/api/mutations/useUpdateDocChronologyTitle';
import { useGetDocChronology } from '@/api/queries/useGetDocChronology';
import { useGetDocChronologyDocs } from '@/api/queries/useGetDocChronologyDocs';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetFilterOptions } from '@/api/queries/useGetMetadataFilterOptions';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { DownloadDialog } from '@/new/components/dialogs/download-dialog';
import { DocChronologyExportProgressModal } from '@/new/components/dialogs/export-progress-dialog/doc-chronology-export-progress';
import { EditFieldDialog } from '@/new/components/edit-field-dialog';
import { ExportReadyDialog } from '@/new/components/export-ready-dialog';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Pagination } from '@/new/components/pagination';
import { Button } from '@/new/components/shadcn/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { Skeleton } from '@/new/components/skeleton';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';

export function DocChronology() {
  const { matterId, subsetId, docChronologyId } = useParams();

  if (!matterId || !subsetId || !docChronologyId) return null;

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Doc Chronologies', path: 'chronologies/doc' },
  });

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  // Export state
  const [downloadModalIsOpen, setDownloadModalIsOpen] = useState(false);
  const [exportProgressModalIsOpen, setExportProgressModalIsOpen] = useState(false);
  const [isExportCreating, setIsExportCreating] = useState(false);
  const [exportError, setExportError] = useState<string>();
  const [currentExportId, setCurrentExportId] = useState<string | null>(null);

  const { downloadMutation, downloadWithDocuments } = useDocChronologyDownload({
    docChronologyId,
    matterId,
    subsetId,
  });
  const { mutateAsync: fetchDownload, isLoading: isLoadingDownload } = downloadMutation;

  const { data: docChronology } = useGetDocChronology({
    matterId,
    subsetId,
    docChronologyId,
  });

  const { mutate: updateDocChronology } = useUpdateDocChronology();

  // Relativity field selection state (shared across all popovers)
  const [localSelectedRelativityFields, setLocalSelectedRelativityFields] = useState<Set<string>>(new Set());

  // Sync local state when docChronology changes
  useEffect(() => {
    if (docChronology?.relativityFields) {
      setLocalSelectedRelativityFields(new Set(docChronology.relativityFields));
    }
  }, [docChronology?.relativityFields]);

  const {
    data,
    isLoading: isLoadingDocs,
    isPreviousData,
  } = useGetDocChronologyDocs({
    matterId,
    subsetId,
    docChronologyId,
    page: pageIndex + 1,
    pageSize,
  });

  // Fetch filter options so users can toggle metadata fields
  const { data: filterOptions } = useGetFilterOptions({ matterId });

  // Pagination
  const goToPage = (val: number) => {
    const newPaginationState = { pageIndex: val, pageSize: pageSize };
    setPagination(newPaginationState);
  };
  const prevPage = () => goToPage(pageIndex - 1);
  const nextPage = () => goToPage(pageIndex + 1);
  const totalPages = data?.pageCount;

  // Export handlers
  const handleDownloadTable = useCallback(
    async ({
      title,
      columns,
      relativityColumns,
      downloadFormat,
      includeSourceDocuments,
    }: {
      title: string;
      columns: string[];
      relativityColumns?: string[];
      downloadFormat: DownloadFormat;
      includeSourceDocuments?: boolean;
    }) => {
      const typedColumns = columns as DocChronologyDownloadColumn[];
      if (includeSourceDocuments) {
        try {
          const result = await fetchDownload({
            title,
            columns: typedColumns,
            relativityColumns,
            styleFormat: StyleFormat.Table,
            downloadFormat,
            includeSourceDocuments,
          });

          if (result) {
            setDownloadModalIsOpen(false);
            setCurrentExportId(result.exportId);
            setExportProgressModalIsOpen(true);
            setIsExportCreating(true);
            setExportError(undefined);
          }
        } catch (error) {
          setDownloadModalIsOpen(false);
          setExportError(error instanceof Error ? error.message : 'Export failed');
          setExportProgressModalIsOpen(true);
          setIsExportCreating(false);
        }
      } else {
        await fetchDownload({
          title,
          columns: typedColumns,
          relativityColumns,
          styleFormat: StyleFormat.Table,
          downloadFormat,
          includeSourceDocuments: false,
        });
        setDownloadModalIsOpen(false);
      }
    },
    [fetchDownload],
  );

  const handleDownloadWritten = useCallback(
    async ({
      title,
      style,
      downloadFormat,
      documentOptions,
      includeSourceDocuments,
    }: {
      title: string;
      downloadFormat: DownloadFormat;
      style?: string;
      documentOptions?: DocumentDownloadOptions;
      includeSourceDocuments?: boolean;
    }) => {
      if (includeSourceDocuments) {
        try {
          const result = await fetchDownload({
            title,
            style,
            styleFormat: StyleFormat.Written,
            downloadFormat,
            documentOptions,
            includeSourceDocuments,
          });

          if (result) {
            setDownloadModalIsOpen(false);
            setCurrentExportId(result.exportId);
            setExportProgressModalIsOpen(true);
            setIsExportCreating(true);
            setExportError(undefined);
          }
        } catch (error) {
          setDownloadModalIsOpen(false);
          setExportError(error instanceof Error ? error.message : 'Export failed');
          setExportProgressModalIsOpen(true);
          setIsExportCreating(false);
        }
      } else {
        await fetchDownload({
          title,
          style,
          styleFormat: StyleFormat.Written,
          downloadFormat,
          documentOptions,
          includeSourceDocuments: false,
        });
        setDownloadModalIsOpen(false);
      }
    },
    [fetchDownload],
  );

  return (
    <>
      <Breadcrumb
        items={[
          ...breadcrumbItems.map((item, index) =>
            index === breadcrumbItems.length - 1 ? { ...item, active: false } : item,
          ),
          {
            href: `/matters/${matterId}/subsets/${subsetId}/chronologies/doc/${docChronologyId}`,
            label: docChronology?.title ?? 'Chronology',
            active: true,
          },
        ]}
      />

      <PageContainer>
        <PageHeader>
          {docChronology ? (
            <div className="flex items-center gap-3.5">
              <PageTitle text={docChronology.title} iconClass="lucide--folder-git-2" />
              <EditFieldDialog
                triggerClassName="text-base"
                currentValue={docChronology.title}
                onSave={(title) => updateDocChronology({ matterId, subsetId, docChronologyId, title })}
                title="Update Document Chronology"
                description="Change the title of this document chronology."
                fieldLabel="Chronology Title"
                placeholder="Emails between 2010 and 2016..."
              />
            </div>
          ) : (
            <Skeleton className="h-10 w-36" />
          )}

          <div className="flex items-center gap-2">
            <Button
              disabled={!isLoadingDocs && data?.totalCount === 0}
              onClick={() => {
                // If an export is already in progress, show the progress modal directly
                if (currentExportId) {
                  setExportProgressModalIsOpen(true);
                } else {
                  setDownloadModalIsOpen(true);
                }
              }}
            >
              <Icon className="lucide--download" />
              Export Document Chronology
            </Button>
            <Pagination
              canGetPrevPage={pageIndex > 0}
              canGetNextPage={pageIndex < (totalPages ?? 0) - 1}
              prevPage={prevPage}
              nextPage={nextPage}
              currentPage={pageIndex}
              noOfPages={totalPages}
              goToPage={goToPage}
            />
          </div>
        </PageHeader>
        {/* 
        <div className="mb-2 flex items-center justify-between gap-4">
          <div>
            <p className="text-gray-600">
              <span className="font-semibold">{data?.totalCount ?? 0}</span> total{' '}
              {data?.totalCount === 1 ? 'document' : 'documents'}
            </p>
          </div>
        </div> */}

        <div className={`${isPreviousData ? 'opacity-60' : 'opacity-100'} relative pt-1 pl-1 duration-150`}>
          {/* This little div stops a flash of the grey line showing on pagination... */}
          <div className="absolute -top-2.5 left-2.5 z-5 h-18 w-2 bg-white"></div>

          {isLoadingDocs ? (
            <>
              {[...Array(4)].map((_, index) => (
                <div key={index} className="group relative mb-6 flex w-full pl-8.5">
                  <div className="absolute top-11.5 left-0.25 z-10 h-4.25 w-4.25 rounded-full border-2 border-gray-200/80 bg-gray-50" />
                  <DocChronRowSkeleton />
                </div>
              ))}
            </>
          ) : !data?.docs?.length ? (
            <div className="flex w-full items-center justify-center py-12">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Icon className="lucide--folder-git-2" />
                  </EmptyMedia>
                  <EmptyTitle>This chronology has no documents</EmptyTitle>
                  <EmptyDescription className="text-wrap">
                    This document chronology does not contain any documents.{' '}
                    <Link
                      to={`/matters/${matterId}/subsets/${subsetId}/docs`}
                      className="text-sm text-blue-500 hover:underline"
                    >
                      Add more documents
                    </Link>
                    .
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          ) : (
            data?.docs?.map((doc) => (
              <div key={doc.id} className="group relative mb-6 flex w-full pl-8.5">
                {/* Timeline circles and lines */}
                <div className="absolute top-11.5 left-0.25 z-10 h-4.25 w-4.25 rounded-full border-2 border-teal-400 bg-teal-50" />
                <div className="absolute inset-0 -inset-y-3 w-px translate-x-[9px] bg-gray-200 group-first:top-11.5 group-last:bottom-[calc(100%-46px)]"></div>

                <DocChronRow
                  chronDoc={doc}
                  docChronology={docChronology}
                  localSelectedRelativityFields={localSelectedRelativityFields}
                  setLocalSelectedRelativityFields={setLocalSelectedRelativityFields}
                />
              </div>
            ))
          )}
        </div>
      </PageContainer>

      <DownloadDialog
        isOpen={downloadModalIsOpen}
        onClose={() => setDownloadModalIsOpen(false)}
        modalTitle="Export Document Chronology"
        downloadTitle={docChronology?.title ?? 'Document Chronology'}
        isLoading={isLoadingDownload}
        columnOptions={DocChronologyDownloadColumnOptions as string[]}
        relativityColumnOptions={filterOptions?.relativityFields?.fields}
        onDownloadTable={handleDownloadTable}
        onDownloadWritten={handleDownloadWritten}
        displayOptions={{
          showStyle: true,
          showDocumentOptions: false,
          showSourceDocumentsOption: true,
        }}
        showEmailNotification
      />

      <DocChronologyExportProgressModal
        isOpen={exportProgressModalIsOpen}
        onClose={() => {
          setExportProgressModalIsOpen(false);
          setIsExportCreating(false);
          setExportError(undefined);
        }}
        matterId={matterId}
        subsetId={subsetId}
        docChronologyId={docChronologyId}
        docChronologyTitle={docChronology?.title ?? 'Document Chronology'}
        onDownload={async () => {
          if (currentExportId) {
            await downloadWithDocuments(docChronology?.title ?? 'Document Chronology', currentExportId);
            // Clear exportId after successful download so next export starts fresh
            setCurrentExportId(null);
          }
        }}
        isCreating={isExportCreating}
        error={exportError}
      />

      <ExportReadyDialog
        onDownload={async (exportId) => {
          await downloadWithDocuments(docChronology?.title ?? 'Document Chronology', exportId);
        }}
        message="Your document chronology export is ready to download."
      />
    </>
  );
}
